// angular imports
import { Injectable, OnDestroy } from "@angular/core";
import { NotificationDismissMessage } from "@app/app-common";

// local imports
import {
  AppAvailability,
  AppDataChangedMessage,
  AppDataHttpService,
  AppManagementModel,
  NotificationFactory,
  NotificationState,
  NotificationsStateChangedMessage,
  PackageExportStateHandler,
  ServicesMatrixExportStateHandler,
} from "@app/core";
import { Messenger } from "@app/shared";

@Injectable({
  providedIn: "root",
})
export class AppStateService implements OnDestroy {
  private _initialized = false;
  public AppManagementState: AppManagementModel = new AppManagementModel();
  public NotificationState: NotificationState = new NotificationState();

  private _servicesMatrixExportStateHandler: ServicesMatrixExportStateHandler;
  private _packageExportStateHandler: PackageExportStateHandler;


  constructor(
    private startupHttpService: AppDataHttpService,
    private messenger: Messenger,
    private notificationFactory: NotificationFactory
  ) {
    this._servicesMatrixExportStateHandler = new ServicesMatrixExportStateHandler();
    this._packageExportStateHandler = new PackageExportStateHandler();
  }

  async initialize() {
    if (this._initialized) return;

    await Promise.all([
      this.initializeApplications(),
      this.initializeUserNotifications()
    ]);

    this._initialized = true;
  }

  private initializeUserNotifications = async () => {
    const userNotificationResults = this.startupHttpService.getUserNotifications();
      const userNotificationResponse = await userNotificationResults.response;


      this.NotificationState.notifications.set(userNotificationResponse.notifications.map((dto) => 
          this.notificationFactory.createNotification(dto).getMessage()
      ).reverse());

      this._servicesMatrixExportStateHandler.initialize(this, this.messenger);
      this._packageExportStateHandler.initialize(this, this.messenger);

      this.messenger.subscribe(
        this, NotificationDismissMessage,
        (message) => {
          // Handle notification dismissal
          this.startupHttpService.deleteNotification(message.notificationId).response.then(() => {
            // Remove the notification from the state
            this.NotificationState.notifications.update((notifications) => 
              notifications.filter(n => n.id !== message.notificationId)
            );
          }).catch((error) => {
            console.error("Failed to delete notification:", error);
          });
        }
      );

      this.messenger.send(new NotificationsStateChangedMessage(this.NotificationState));
  }

  public initializeApplications = async () => {
    // This method can be used to initialize any landing page specific data

    if(!this.AppManagementState.apps()) {
      const appsHttpResult = this.startupHttpService.getApps();
      const appResponse = await appsHttpResult.response;
      this.AppManagementState.apps.set(AppAvailability.fromDto(appResponse));
      this.messenger.send(new AppDataChangedMessage(this.AppManagementState));
    }
  }

  ngOnDestroy(): void {
    this.messenger.unsubscribeByOwner(this);
    this._initialized = false;
  }
}
